<template>
  <div class="grid-module settings">
    <h1>Settings</h1>
    <div class="columns" style="position:relative;">
      <div class="column">
        <h2>Service provider agreement</h2>
          <b-field grouped position="is-left" style="align-items: flex-start">
            <b-button class="is-success"
                      @click="needAgreementUpdate"
                      :disabled="!checkAccess(MANAGE_SETTINGS_MODIFY)">
              Agreement Update
            </b-button>
          </b-field>
      </div>
    </div>
    <span class="settings-text--gray">Last updated:{{ lastUsedAgreementUpdateTitle }}</span>
    <b-modal :active.sync="isUpdateAgreementDateActive"
             class="card-form-modal" has-modal-card
             width="450">
      <div class="modal-card">
        <header class="modal-card-head">
          <h2 class="modal-card-title">Agreement Update</h2>
        </header>
        <div class="modal-card-body">
          <validation-observer ref="createCardObserver" v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(frmUpdateServiceAgreementDate)">
              <p>Are you sure you want to <b>update</b> agreement?</p>
              <p>All contractors will have to reaccept it.</p>
            </form>
          </validation-observer>
        </div>
        <footer class="modal-card-foot buttons is-right">
          <div class="card-form__panel">
            <b-button @click="closeCardForm">Cancel</b-button>
            <button
              @click="frmUpdateServiceAgreementDate"
              class="button is-success">Update
            </button>
          </div>
        </footer>
      </div>
    </b-modal>

    <hr>

    <template v-if="checkAccess(MARKETING_MATERIALS_MANAGE_ACCESS)">
      <MarketingMaterials />

      <hr>
    </template>

    <h2>Training and FAQs</h2>
    <SettingsMaterials />
    <template v-if="checkAccess(MANAGE_SETTINGS_MODIFY)">
      <template v-if="hasLeadFieldsPrivilegeModify">
        <hr>
        <h2>Lead Source & First Touch</h2>
        <LeadSource />
      </template>
      <template v-if="hasLeadDistributionPrivilege">
      <hr>
      <h2>Lead Distribution</h2>
        <div v-if="countLeadSourcesUndistributed > 0" class="notification-group">
          <b-notification
                  :closable=false
                  type="alert-block"
                  :message="messageNotDistributed + countLeadSourcesUndistributed + '.'"
          />
          <div class="notification-group-set-up-group">
            <a class="notification-group-ref"
               @click="assignLeadSources">ASSIGN NOW</a>
          </div>
        </div>
      <b-field grouped position="is-left" style="align-items: flex-start">
        <b-button
            class="lead-distribution-add-set-btn is-success"
            @click="addDistributionSet">
          add set
        </b-button>
      </b-field>
      <lead-distribution
          v-if="leadDistribution.length > 0"
          :data="leadDistribution"
          @submit="updateLeadDistributionInfo">
      </lead-distribution>
      <span class="settings-text--gray">
        Last updated: {{ lastUpdatedLeadDistribution.email }},
        {{ lastUpdatedLeadDistribution.paramVal }}
      </span><br>

      <hr>

      <MultipleUsers />
      </template>
    </template>
    <template v-if="hasAccess(
        MANAGE_NEWS_AND_UPDATES_MODIFY,
        MANAGE_NEWS_AND_UPDATES_MODIFY,
        true
        )">
      <hr>
      <NewsAndUpdates > </NewsAndUpdates>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import { mapGetters, mapMutations } from 'vuex';
import * as MUTATIONS from '@/store/mutations';
import LeadDistributionModal from '@/components/Modal/LeadDistribution.vue';
import {
  getAgreementDateSetting,
  setAgreementDate,
  getLeadSources,
  getLeadDistribution,
  getLeadDistributionUpdateSetting,
  postLeadSources,
  getLeadSourcesUpdateSetting,
} from '@/api/settings';
import {
  MANAGE_NEWS_AND_UPDATES_MODIFY,
  MANAGE_SETTINGS_MODIFY,
  MARKETING_MATERIALS_MANAGE_ACCESS,
} from '@/const/privileges';
import SettingsMaterials from './components/Materials.vue';
import LeadSource from './LeadSource/LeadSource.vue';
import LeadDistribution from './components/LeadDistribution.vue';
import MarketingMaterials from './MarketingMaterials/MarketingMaterials.vue';
import MultipleUsers from './components/MultipleUsers.vue';
import NewsAndUpdates from './components/NewsAndUpdates.vue';


export default {
  name: 'Settings',
  components: {
    MultipleUsers,
    LeadSource,
    SettingsMaterials,
    LeadDistribution,
    MarketingMaterials,
    NewsAndUpdates,
  },
  data() {
    return {
      MARKETING_MATERIALS_MANAGE_ACCESS,
      MANAGE_SETTINGS_MODIFY,
      MANAGE_NEWS_AND_UPDATES_MODIFY,
      data: [],
      leadSources: [],
      leadSourcesUndistributed: [],
      leadDistribution: [],
      isUpdateAgreementDateActive: false,
      lastUsedAgreementUpdateTitle: '',
      lastUpdatedLeadDistribution: {
        email: '',
        paramVal: '',
      },
      contractorId: null,
      errorMsg: '',
      lastUsedEmail: '',
      lastUsedDates: null,
      hasLeadDistributionPrivilege: false,
      hasLeadFieldsPrivilegeAccess: false,
      hasLeadFieldsPrivilegeModify: false,
      countLeadSourcesUndistributed: 0,
      messageNotDistributed: 'Number of lead sources, undistributed to the sets: ',
    };
  },

  async mounted() {
    this.hasLeadDistributionPrivilege = this.hasPrivilege({ resource: 'manage-lead-distribution', code: 'access' });
    this.hasLeadFieldsPrivilegeModify = this.hasPrivilege({ resource: 'manage-lead-fields', code: 'modify' });
    this.hasLeadFieldsPrivilegeAccess = this.hasPrivilege({ resource: 'manage-lead-fields', code: 'access' });
    if (this.hasLeadDistributionPrivilege) {
      await this.updateLeadDistributionInfo();
    }
    await this.getAgreementDateSetting();
  },

  computed: {
    ...mapGetters(['hasAccess']),
    ...mapGetters('auth2', ['hasPrivilege']),
  },

  methods: {
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),

    async updateLeadDistributionInfo() {
      await this.getLeadDistribution();
      this.lastUpdatedLeadDistribution = await this.getLeadDistributionLastUpdate();
    },

    async addDistributionSet() {
      this.startLoading();
      const { data: { data } } = await getLeadSources();
      const Component = Vue.extend(LeadDistributionModal);
      await this.$buefy.modal.open({
        parent: this,
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'card-form-modal',
        canCancel: ['outside'],
        props: {
          data,
          mode: 'leadDistributionSet',
        },
        events: {
          submit: () => {
            this.updateLeadDistributionInfo();
          },
        },
      });
      this.changeModalPosition();
      this.finishLoading();
    },

    async assignLeadSources() {
      this.startLoading();
      await this.getLeadDistribution();
      const Component = Vue.extend(LeadDistributionModal);
      await this.$buefy.modal.open({
        parent: this,
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'card-form-modal',
        canCancel: ['outside'],
        props: {
          data: this.leadSourcesUndistributed,
          dataLeadDistribution: this.leadDistribution,
          mode: 'leadSourceAssign',
        },
        events: {
          submit: () => {
            this.updateLeadDistributionInfo();
          },
        },
      });
      this.changeModalPosition();
      this.finishLoading();
    },

    async getLeadSourceLastUpdate() {
      const { data } = await getLeadSourcesUpdateSetting();
      return _.get(data, 'data.0', 'Invalid Response');
    },

    async getLeadDistributionLastUpdate() {
      const { data } = await getLeadDistributionUpdateSetting();
      return _.get(data, 'data.0', 'Invalid Response');
    },

    async getLeadDistribution() {
      this.startLoading();

      try {
        // get lead distribution
        const { data: leadDistributionData } = await getLeadDistribution();
        this.leadDistribution = leadDistributionData;

        // get lead sources undistributed
        if (this.hasLeadFieldsPrivilegeAccess) {
          const {
            data: { total, data: leadSourcesUndistributedData },
          } = await getLeadSources(false);
          this.leadSourcesUndistributed = leadSourcesUndistributedData;
          this.countLeadSourcesUndistributed = total;
        }
      } catch (error) {
        this.$buefy.snackbar.open({
          message: 'An unexpected error occurred, please contact support@enhancify.com',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.finishLoading();
    },

    async onLeadSourceSubmit(payload) {
      this.startLoading();

      try {
        const { data } = await postLeadSources(payload);
        if ('error' in data) {
          this.toast(_.get(data, 'error'), 'is-danger');
        } else if (_.some(data, (row) => _.get(row, 'double', false))) {
          this.leadSources = data;
        } else {
          this.leadSources = [];
        }
        const responseLastUpdate = await getLeadSourcesUpdateSetting();
        this.lastUpdatedLeadSource = _.get(responseLastUpdate, 'data.data.0');
      } catch (e) {
        this.toast('Something wrong', 'is-danger');
      }

      this.finishLoading();
    },

    async getAgreementDateSetting() {
      const { data } = await getAgreementDateSetting();
      this.data = data;
      this.agreementData = this.data.data;
      this.lastUsedAgreementUpdateTitle = this.formatAgreementLastUpdate(data);
    },

    formatAgreementLastUpdate(data) {
      if (data.total === 1 && _.get(data, 'data.0.userId')) {
        return `${_.get(data, 'data.0.email')}, ${_.get(data, 'data.0.paramVal')}`;
      }
      return '';
    },

    checkAccess(privilege) {
      return this.hasAccess(privilege, privilege, true);
    },

    needAgreementUpdate() {
      this.isUpdateAgreementDateActive = true;
    },

    closeCardForm() {
      this.isUpdateAgreementDateActive = false;
    },

    frmUpdateServiceAgreementDate() {
      setAgreementDate(this.agreementData[0].paramId).then((response) => {
        if (response.data.error) {
          this.toast(response.data.message, 'is-danger');
        } else {
          this.lastUsedAgreementUpdateTitle = ` ${response.data.data.email}, `;
          this.lastUsedAgreementUpdateTitle += response.data.data.paramVal;
          this.toast('Service agreement date is update');
        }
      })
        .catch(() => {
          this.toast('Service agreement date update error', 'is-danger');
        })
        .finally(() => {
          this.isUpdateAgreementDateActive = false;
        });
    },

    toast(message, type = 'is-success') {
      this.$buefy.toast.open({ message, type, duration: 5000 });
    },

    changeModalPosition() {
      const grid = document.querySelector('.grid-module');
      const modal = document.querySelector('.modal');
      if (grid && modal) {
        grid.insertAdjacentElement('beforeend', modal);
      }
    },
  },
};
</script>

<style lang="stylus">
@require "../../../assets/style/modern/variables.styl";

.rawinput:not(:focus)
  border none !important
  box-shadow none !important
  outline none !important
  -moz-appearance:textfield;

.rawinput::-webkit-outer-spin-button,
.rawinput::-webkit-inner-spin-button
  -webkit-appearance: none;
  margin: 0;

.settings
  & .settings-text--gray
    color: $colors.text-gray
</style>

<style lang="stylus" scoped>
.lead-distribution-add-set-btn
  text-transform uppercase
.not-distributed-wrapper
  color red
  border-left 3px solid
  padding 10px
.notification-group
  display flex
  align-items baseline
.notification-group-ref
  padding-right 10px
  color #525F66
  font-weight bold
  position relative
.notification-group-ref::before
  content url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='14' cy='14' r='13.5' fill='white' stroke='%23525F66'/%3E%3Cpath d='M20.3536 14.3536C20.5488 14.1583 20.5488 13.8417 20.3536 13.6464L17.1716 10.4645C16.9763 10.2692 16.6597 10.2692 16.4645 10.4645C16.2692 10.6597 16.2692 10.9763 16.4645 11.1716L19.2929 14L16.4645 16.8284C16.2692 17.0237 16.2692 17.3403 16.4645 17.5355C16.6597 17.7308 16.9763 17.7308 17.1716 17.5355L20.3536 14.3536ZM7 14.5H20V13.5H7V14.5Z' fill='%23525F66'/%3E%3C/svg%3E")
  position absolute
  top -3px
  left 100px
.notification-group-ref:hover::before
  content url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='14' cy='14' r='13.5' fill='%23525F66' stroke='%23525F66'/%3E%3Cpath d='M20.3536 14.3536C20.5488 14.1583 20.5488 13.8417 20.3536 13.6464L17.1716 10.4645C16.9763 10.2692 16.6597 10.2692 16.4645 10.4645C16.2692 10.6597 16.2692 10.9763 16.4645 11.1716L19.2929 14L16.4645 16.8284C16.2692 17.0237 16.2692 17.3403 16.4645 17.5355C16.6597 17.7308 16.9763 17.7308 17.1716 17.5355L20.3536 14.3536ZM7 14.5H20V13.5H7V14.5Z' fill='white'/%3E%3C/svg%3E")
.notification-group-set-up-group
  min-width 120px
.grid-module
  height auto
  .file-selector-with-button
    display flex
    @media screen and (max-width: 630px)
      flex-wrap wrap
    .button
      margin-left 0.5rem
.template-selector-wrapper
  width 100%;
.template-selector >>> span
  width: 100%
  select
    width 100%
.personal-info__field.block-title
  display flex
  align-items baseline
  margin-bottom 0
.columns >>> .mb-0
  margin-bottom:0
@media screen and (min-width: 769px)
  .template-selector-buttons
    margin-top: 36px
</style>
